<template>
    <div class="animated fadeIn">
        <LoadingWidget v-if="initStatus != 1" :status="initStatus" @init="init" />
        <b-card v-else>
            <h5 class="mb-0"><i class="fa fa-fw fa-list mr-2" />Daftar Transaksi</h5>
            <hr />
            <b-row>
                <b-col md="2">
                    <b-form-group label="Tipe Transaksi">
                        <b-form-select :options="optType" v-model="filter.type" value-field="id" text-field="name" />
                    </b-form-group>
                </b-col>
                <b-col md="3">
                    <b-form-group label="Dari Tanggal">
                        <b-input type="date" v-model="filter.startDate" />
                    </b-form-group>
                </b-col>
                <b-col md="3">
                    <b-form-group label="Sampai Tanggal">
                        <b-input type="date" v-model="filter.endDate" />
                    </b-form-group>
                </b-col>
                <b-col md="4">
                    <b-form-group label="Customer">
                        <multiselect v-model="filter.customer" label="name" track-by="id" :internal-search="true"
                            deselect-label="" select-label="" :options="optCustomer" />
                    </b-form-group>
                </b-col>
            </b-row>
            <b-row>
                <b-col md="8">
                    <b-button v-if="isPembayaranPiutangDownload" variant="dark" @click="preparePayment">
                        <i class="fa fa-plus mr-2" />Pembayaran Piutang
                    </b-button>
                    <b-button v-if="isSettleHutangDeposit" variant="dark" class="ml-2" @click="prepareDebt">
                        <i class="fa fa-plus mr-2" />Settle Hutang / Deposit
                    </b-button>
                </b-col>

                <b-col md="4" class="text-right">
                    <b-button v-if="isPembayaranPiutangDownload" class="mr-2" variant="success" @click="showDownload">
                        <i class="fa fa-download mr-2" />Download List Piutang
                    </b-button>
                    <b-button variant="primary" @click="getItems">
                        <i class="fa fa-search mr-2" />Cari
                    </b-button>
                </b-col>
            </b-row>
            <b-table bordered hover show-empty no-local-sorting :busy="isBusy" :items="items" :fields="fields"
                @sort-changed="onSortChanged" responsive class="my-3">
                <template v-slot:empty>
                    Tidak ada data transaksi.
                </template>
                <template v-slot:cell(customer)="row">
                    {{ row.item.customer.name }}
                    <div class="small text-muted">
                        {{ row.item.customer.phone }}
                    </div>
                </template>
                <template v-slot:cell(type)="row">
                    <b-badge :variant="parseType(row.item.type, 0)">{{ parseType(row.item.type, 1) }}</b-badge>
                    <div class="small text-muted">
                        {{ row.item.params || "-" }}
                    </div>
                </template>
                <template v-slot:cell(created_at)="row">
                    {{ $helper.parseDate(row.item.created) }}, {{ $helper.parseHourMinute(row.item.created) }}
                    <div class="small text-muted">
                        {{ row.item.created_by ? row.item.created_by.fullname : "-" }}
                    </div>
                </template>
                <template v-slot:cell(amount)="row">
                    Rp. {{ $helper.formatThousand(row.item.amount) }}
                </template>
                <template v-slot:cell(credit)="row">
                    <div v-if="isMoneyIn(row.item.type)">
                        Rp. {{ $helper.formatThousand(row.item.credit_change) }}
                    </div>
                    <div v-else class="text-danger">
                        Rp. {{ $helper.formatThousand(row.item.credit_change * -1) }}
                    </div>
                    <div class="text-muted small">
                        Setelah: Rp. {{ $helper.formatThousand(row.item.credit_after) }}
                    </div>
                </template>
                <template v-slot:cell(debt)="row">
                    <div v-if="isMoneyIn(row.item.type)" class="text-danger">
                        Rp. {{ $helper.formatThousand(row.item.debt_change * -1) }}
                    </div>
                    <div v-else>
                        Rp. {{ $helper.formatThousand(row.item.debt_change) }}
                    </div>
                    <div class="text-muted small">
                        Setelah: Rp. {{ $helper.formatThousand(row.item.debt_after) }}
                    </div>
                </template>
            </b-table>
            <PageSizer :totalRows="totalRows" :page="currentPage" :options="options" @limit-change="onLimitChange"
                @page-change="onPageChange" />
        </b-card>
        <b-modal id="m-payment" title="Pembayaran Piutang" no-close-on-backdrop no-close-on-esc hide-footer>
            <b-form-group label="Customer">
                <multiselect v-model="input.customer" label="name" track-by="id" :internal-search="true" deselect-label=""
                    select-label="" :options="optCustomerForTransaction" />
            </b-form-group>
            <b-form-group class="mt-3" label="Jumlah Pembayaran"
                description="Jumlah melebihi piutang akan dimasukan sebagai deposit / hutang.">
                <b-input-group prepend="Rp.">
                    <b-input v-model="input.payment" maxlength="20" @input="onPaymentInput" />
                </b-input-group>
            </b-form-group>
            <b-form-group class="mt-3" label="Catatan Pembayaran (Opsional)">
                <b-input v-model="input.note" maxlength="200" />
            </b-form-group>
            <div class="text-right">
                <b-button variant="primary" :disabled="isSubmitting" @click="execPayment">
                    {{ isSubmitting ? "Menyimpan..." : "Simpan" }}
                </b-button>
            </div>
        </b-modal>
        <b-modal id="m-payment-success" title="Pembayaran Berhasil" centered hide-footer no-close-on-backdrop
            no-close-on-esc>
            Pembayaran sebesar Rp. {{ $helper.formatThousand(successData.amount) }} berhasil disimpan dengan perincian
            sebagai berikut:
            <ul class="mt-2">
                <li v-for="item in successData.invoices" v-bind:key="item.no_faktur">
                    {{ item.no_faktur }} - <strong>Rp. {{ $helper.formatThousand(item.amount) }} <span
                            v-if="item.is_settled == true">[LUNAS]</span></strong>
                </li>
                <li v-if="successData.debt != 0">
                    Sebagai deposit / hutang ke customer - <strong>Rp. {{ $helper.formatThousand(successData.debt)
                    }}</strong>
                </li>
            </ul>
            <div class="mt-4 text-right">
                <b-button variant="primary" @click="dismissAndRefresh">
                    OK
                </b-button>
            </div>
        </b-modal>
        <b-modal id="m-debt-settlement" title="Pelunasan Hutang / Deposit Customer" no-close-on-backdrop no-close-on-esc
            hide-footer>
            <b-form-group label="Customer">
                <multiselect v-model="input.customer" label="name" track-by="id" :internal-search="true" deselect-label=""
                    select-label="" :options="optCustomerForTransaction" />
            </b-form-group>
            <b-form-group class="mt-3" label="Catatan Pembayaran">
                <b-input v-model="input.note" maxlength="200" />
            </b-form-group>
            <div class="text-right">
                <b-button variant="primary" :disabled="isSubmitting" @click="execSettleDebt">
                    {{ isSubmitting ? "Menyimpan..." : "Simpan" }}
                </b-button>
            </div>
        </b-modal>
        <b-modal id="m-download" title="Download List Piutang" centered no-close-on-backdrop no-close-on-esc hide-footer>
            <div v-if="status == 0">
                Menyiapkan laporan, mohon menunggu...
            </div>
            <div v-else-if="status == -1">
                Gagal menyiapkan laporan. Silahkan coba lagi, atau perpendek jarak antara tanggal mulai dan selesai laporan!
            </div>

            <div v-else>
                Laporan sudah siap. Tekan tombol dibawah ini untuk download laporan Excel.
                <div class="mt-2">
                    <a :href="$BASE_FILE_PATH + path" download>
                        <b-button variant="primary">
                            <i class="fa fa-download mr-2" />Download Excel List Piutang
                        </b-button>
                    </a>
                </div>
            </div>
        </b-modal>
    </div>
</template>

<script>
import EnumPermissions from "@/shared/enum-permissions"

export default {
    data() {
        return {

            initStatus: 0,
            isBusy: true,
            isSubmitting: false,
            show: 10,

            successData: {},

            filter: {
                type: -1,
                customer: null,
                startDate: "",
                endDate: ""
            },

            input: {
                customer: null,
                payment: "",
                note: ""
            },

            options: [
                { value: 10, text: '10' },
                { value: 20, text: '20' },
                { value: 30, text: '30' },
                { value: 40, text: '40' },
                { value: 50, text: '50' },
            ],

            fields: [
                { key: "customer", label: "Customer", sortable: true },
                { key: "type", label: "Tipe", sortable: true },
                { key: "created_at", label: "Eksekusi pada / oleh", sortable: true },
                { key: "amount", label: "Nominal Transaksi", sortable: true },
                { key: "credit", label: "Piutang Customer", sortable: true },
                { key: "debt", label: "Sisa Deposit Customer", sortable: true }
            ],

            items: [],
            activeItem: {},

            currentPage: 1,
            perPage: 10,
            totalRows: 0,
            sortBy: null,
            sortDesc: false,

            optType: [
                { id: -1, name: "Semua" },
                { id: 1, name: "Pembelian" },
                { id: 2, name: "Retur" },
                { id: 3, name: "Pembayaran" },
                { id: 4, name: "Pelunasan Hutang" }
            ],

            optCustomer: [],
            optCustomerForTransaction: [],
            status: 0,

        }
    },

    computed: {
        isPembayaranPiutangDownload() {
            return localStorage.getItem("permissions").includes(EnumPermissions.PEMBAYARAN_PIUTANG) || localStorage.getItem("roleName").includes('Superadmin')
        },
        isSettleHutangDeposit() {
            return localStorage.getItem("permissions").includes(EnumPermissions.SETTLE_HUTANG_DEPOSIT) || localStorage.getItem("roleName").includes('Superadmin')
        }
    },

    methods: {

        init() {

            this.initStatus = 0
            let getItems = this.getItems()

            let getCustomers = this.$axios.get("customer/selection").then(res => {
                this.optCustomer = res.data.data
            })

            Promise.all([getItems, getCustomers]).then(() => {
                this.initStatus = 1
            })
                .catch(error => {
                    this.initStatus = -1
                })

        },

        getItems(page = 1) {

            this.isBusy = true
            this.items = []

            if (isNaN(page)) {
                this.currentPage = 1
            }
            else {
                this.currentPage = page
            }

            // URL builder
            let param = this.$helper.getBasePagedQuery(this.currentPage, this.perPage)

            if (this.filter.type != -1) {
                param += "&filterType=" + this.filter.type
            }

            if (!this.$helper.isBlank(this.filter.startDate)) {
                param += "&filterStartDate=" + this.filter.startDate
            }

            if (!this.$helper.isBlank(this.filter.endDate)) {
                param += "&filterEndDate=" + this.filter.endDate
            }

            if (this.filter.customer != null) {
                param += "&filterCustomer=" + this.filter.customer.id
            }

            if (this.sortBy != null) {
                param += "&sortBy=" + this.sortBy + "&sortDesc=" + this.sortDesc
            }

            return this.$axios.get("payment" + param).then(res => {
                this.items = res.data.data.items
                this.totalRows = res.data.data.total
                sessionStorage.setItem("listTransactionFilter", JSON.stringify(this.filter))
            })
                .catch(error => {
                    this.$helper.parseError(this, error)
                })
                .finally(() => {
                    this.isBusy = false
                })

        },

        parseType(type, index) {
            switch (type) {
                case 1: return ["primary", "PEMBELIAN"][index]
                case 2: return ["warning", "RETUR"][index]
                case 3: return ["success", "PEMBAYARAN"][index]
                case 4: return ["dark", "PELUNASAN HUTANG"][index]
                default: return ["light", "-"][index]
            }
        },

        preparePayment() {

            this.$bvModal.show("m-payment")

            this.$axios.get("payment/customer-for-transactions?mode=1").then(res => {
                this.optCustomerForTransaction = res.data.data

            })
                .catch(error => this.$helper.parseError(this, error))

        },

        execPayment() {

            if (this.input.customer == null) return this.$helper.toastError(this, "Mohon memilih customer!")

            let cleaned = String(this.input.payment).replace(/\./g, "")
            let amountPaid = Number(cleaned)
            if (amountPaid <= 0) {
                return this.$helper.toastError(this, "Jumlah pembayaran harus lebih besar dari nol!")
            }

            let isConfirmed = confirm("Lakukan input pembayaran piutang untuk " + this.input.customer.name + " sebesar Rp. " + this.input.payment + "?")
            if (!isConfirmed) return

            this.isSubmitting = true

            let payload = {
                amount: amountPaid,
                note: this.input.note,
                customerId: this.input.customer.id
            }

            this.$axios.post("payment", payload).then(res => {

                let data = res.data.data
                this.successData = data
                this.$bvModal.hide("m-payment")
                this.$bvModal.show("m-payment-success")
                this.input.customer = null
                this.input.payment = ""
                this.input.note = ""

            })
                .catch(error => this.$helper.parseError(this, error))
                .finally(() => this.isSubmitting = false)

        },

        prepareDebt() {

            this.$bvModal.show("m-debt-settlement")

            this.$axios.get("payment/customer-for-transactions?mode=2").then(res => {
                this.optCustomerForTransaction = res.data.data

            })
                .catch(error => this.$helper.parseError(this, error))

        },

        execSettleDebt() {

            if (this.input.customer == null) {
                return this.$helper.toastError(this, "Mohon memilih customer!")
            }

            if (this.$helper.isBlank(this.input.note)) {
                return this.$helper.toastError(this, "Mohon mengisi catatan pembayaran!")
            }

            let isConfirmed = confirm("Lakukan pelunasan hutang untuk " + this.input.customer.name + " ?")
            if (!isConfirmed) return

            this.isSubmitting = true

            let payload = {
                note: this.input.note,
                customerId: this.input.customer.id
            }

            this.$axios.post("payment/settle-debt", payload).then(res => {

                this.$bvModal.hide("m-debt-settlement")
                this.$helper.toastSuccess(this, "Pelunasan hutang berhasil dilakukan!")
                this.getItems(this.currentPage)
                this.input.customer = null
                this.input.payment = ""
                this.input.note = ""

            })
                .catch(error => this.$helper.parseError(this, error))
                .finally(() => this.isSubmitting = false)

        },

        isMoneyIn(type) {
            return [1, 4].includes(type)
        },

        onPaymentInput() {
            let cleaned = String(this.input.payment).replace(/\./g, "")
            let res = cleaned.replace(/(?!^-)[^0-9]/g, "").replace(/\B(?=(\d{3})+(?!\d))/g, ".")
            this.$nextTick(() => this.input.payment = res)
        },

        dismissAndRefresh() {
            this.$bvModal.hide('m-payment-success')
            this.getItems(this.currentPage)
        },

        onLimitChange(newPerPage) {
            this.perPage = newPerPage
            this.getItems(this.currentPage)
        },

        onPageChange(newPage) {
            this.currentPage = newPage
            this.getItems(this.currentPage)
        },

        onSortChanged(ctx) {
            this.sortBy = ctx.sortBy
            this.sortDesc = ctx.sortDesc
            this.getItems()
        },
        showDownload() {
            this.$bvModal.show('m-download')
            this.status = 0
            this.path = ""
            let payload = {
                mode: 1,
            }
            this.$axios.post("report/customer-transaction", payload).then(res => {
                this.status = 1
                this.path = res.data.data.path
            })
                .catch(error => {
                    this.status = -1
                })
        }
    },
    created() {
        if (!this.$helper.isBlank(sessionStorage.getItem("listTransactionFilter"))) {
            this.filter = JSON.parse(sessionStorage.getItem("listTransactionFilter"))
        }
        this.init()
    }
}
</script>

<style></style>
